// Bench supports in the plan: the Bench support tool (a press, a row dragged
// out), the selected support, its figures and their typed values, its drags, its
// panel, and the supports following their benches after an edit.
// docs/claude/benches.md.
import {
  type BenchSupport,
  type ProductInstance,
  type Vec2,
} from '../../shared/types';
import {
  benchSupportMarks,
  keepSupportsRelative,
  supportMarkAt,
  type SupportPlaces,
  supportPlaces,
  supportRunAt,
  supportSpacings,
  type TrestleMark,
} from '../benchSolids';
import {
  type BenchSpans,
  benchSpans,
  evenSupports,
  followBenches,
  placeSupport,
  pruneBenchSupports,
  supportRow,
  type SupportRun,
  supportsAlong,
  TRESTLE_WIDTH,
} from '../benchSupports';
import {
  beginGesture,
  changes,
  commitGesture,
  drawingRev,
  dropDrag,
  instLocked,
  invalidate,
  newId,
  pushHistory,
  renderProperties,
  startDrag,
  state,
  updateStatus,
  vp,
} from './core';
import {
  distance,
  instanceToWorld,
  pointInPolygon,
  toInstanceLocal,
} from '../geometry';
import { tr } from '../i18n';
import { isPolyRoom } from '../polyRoom';
import { liveFeatureOf } from '../roomFeatures';
import {
  dropFeature,
  featureOf,
  selectFeature,
  selectOne,
  selectRoomKeepingFeature,
} from '../selection';
import {
  type BenchHover,
  innerWallsOf,
  instancesAt,
  SNAP_PX,
} from '../main';
import {
  buttonRow,
  deleteRow,
  infoRow,
  numberRow,
  sectionTitle,
  selectRow,
} from './panelRows';
import {
  pxAt,
  type ToolAim,
} from '../toolAim';
import {
  downSculptTrestle,
  sculptTrestleHoverAt,
} from './sculptTrestleTool';

/** This module's state that main.ts reads and writes too. */
export const benchSupportTool = {
  /** The Bench support tool's press on a bench, dragged out into a row. */
  supportRowDrag: null as { instId: string; run: SupportRun; runIndex: number; s0: number; s1: number; press: Vec2; mmPerPx: number } | null,
  /** Where the cursor is (world) while the Bench support tool is armed, and
   *  whether Alt is held (a support placed free of the bench's end). */
  supportAim: null as ToolAim | null,
  supportAlt: false,
  /** A placed support slid along its bench, by the trestle pressed (`runIndex`,
   *  the run its figures are read on) — `start` is the support as it was, for Esc.
   *  It moves once the drag has gone 4 px from its `press`; under the Bench support
   *  tool a press that never moved removes it. */
  supportMoveDrag: null as { instId: string; id: string; run: SupportRun; runIndex: number; press: Vec2; moved: boolean; removeOnClick: boolean; start: BenchSupport } | null,
};

let supportRevision = -1;
let supportDragRevision = -1;
/** Each room's wall bench spans as its supports last saw them: an edit since
 *  is what `followBenches` makes them follow. */
let supportSpanBase = new Map<string, BenchSpans>();
/** Where each support stood along its runs then (`supportPlaces`): a run an
 *  edit lengthened or shortened takes its supports along (`keepSupportsRelative`). */
let supportPlaceBase = new Map<string, SupportPlaces>();
export function supportsBaseline(): void {
  supportSpanBase = new Map(state.drawing.instances.filter((i) => isPolyRoom(i)).map((i) => [i.id, benchSpans(i)]));
  supportPlaceBase = new Map(state.drawing.instances.filter((i) => i.benchSupports?.length).map((i) => [i.id, supportPlaces(i)]));
}
/** A support keeps its share of each bench run it stands on as the run changes
 *  length — every frame while a drag reshapes it, so its trestles go with it. */
function keepSupportsInPlace(): void {
  for (const inst of state.drawing.instances) {
    const base = supportPlaceBase.get(inst.id);
    if (base && inst.benchSupports) keepSupportsRelative(inst, base);
  }
}
/** After a recorded edit: every support keeps its place along its bench
 *  (`keepSupportsInPlace`; live during a drag, the rest never while a drag is
 *  live, so a drag past a support and back changes nothing) and follows it — a
 *  shortened or split one pulls its supports back onto it —, then the ones no
 *  bench stands on any more (their bench deleted) go, inside the edit's own undo
 *  step. */
export function reconcileSupports(): void {
  if (state.dragMode !== 'none') {
    if (drawingRev() === supportDragRevision) return;
    keepSupportsInPlace();
    changes.touch();
    supportDragRevision = drawingRev();
    return;
  }
  if (drawingRev() === supportRevision) return;
  keepSupportsInPlace();
  for (const inst of state.drawing.instances) {
    if (!inst.benchSupports) continue;
    const base = supportSpanBase.get(inst.id);
    if (base) followBenches(inst, base);
    pruneBenchSupports(inst);
  }
  supportsBaseline();
  changes.touch();
  supportRevision = drawingRev();
}

// ---- Bench supports (the Bench support tool) -----------------------------
/** A support figure the inline editor retypes: the support's trestle on run
 *  `run` moves to `from + side · value` (`SupportSpacing`). */
export interface SupportFigureEdit { instId: string; id: string; run: number; from: number; side: 1 | -1 }
type SupportFigure = { a: Vec2; b: Vec2; length: number; edit?: SupportFigureEdit };
/** The support figures drawn this frame (`supportFigureAt` indexes them). */
export let supportFiguresShown: SupportFigure[] = [];

/** A point in the frame a bench's supports are measured in — a room's local
 *  frame, a standalone sauna bench's world — and back. */
export const supportFrame = (inst: ProductInstance, world: Vec2): Vec2 => (inst.category === 'saunabench' ? world : toInstanceLocal(inst, world));
export const supportWorld = (inst: ProductInstance, p: Vec2): Vec2 => (inst.category === 'saunabench' ? p : instanceToWorld(inst, p));

/** The selected support, while its room or bench is the selection and can be
 *  edited (not frozen, not on a locked layer). */
export function selectedSupportNow(): { inst: ProductInstance; sup: BenchSupport } | null {
  const f = liveFeatureOf(state, state.drawing.instances, 'support');
  const sup = f && !instLocked(f.room) ? f.room.benchSupports?.find((s) => s.id === f.sel.id) : undefined;
  return f && sup ? { inst: f.room, sup } : null;
}

/** Where `inst`'s supports' trestles stand on the plan (its support frame). */
export function supportMarksOf(inst: ProductInstance, extra: BenchSupport[] = []): TrestleMark[] {
  return benchSupportMarks(inst, { benchTierHeights: inst.benchTierHeights }, extra);
}

/** The trestle of support `id` its figures and panel read: on run `run` when it
 *  stands there, else its first. */
function supportMark(inst: ProductInstance, id: string, run?: number): TrestleMark | null {
  const marks = supportMarksOf(inst).filter((m) => m.support === id && m.sr);
  return marks.find((m) => m.run === run) ?? marks[0] ?? null;
}

/** What lies under the cursor for a support: a placed one (its id and the
 *  trestle pressed, within a few px), else a slatted bench run one could be
 *  placed on — on the topmost room or standalone sauna bench there that can be
 *  edited, or only on `only`. */
export function supportTargetAt(world: Vec2, only: ProductInstance | null | undefined, mmPerPx: number): { inst: ProductInstance; p: Vec2; hit: string | null; mark: TrestleMark | null; run: SupportRun | null; runIndex: number } | null {
  const tol = 6 * mmPerPx;
  const list = only ? [only] : instancesAt(world, false).map((id) => state.drawing.instances.find((i) => i.id === id)!);
  for (const inst of list) {
    if (!inst || instLocked(inst)) { if (inst && isPolyRoom(inst)) return null; continue; }
    const room = isPolyRoom(inst) && !!inst.autoBenches;
    if (!room && inst.category !== 'saunabench') continue;
    const p = supportFrame(inst, world);
    if (room && !pointInPolygon(p, inst.polygon!)) continue;
    const mark = supportMarkAt(supportMarksOf(inst), p, tol);
    const on = mark ? null : supportRunAt(inst, p);
    const run = on?.sr ?? null;
    if (mark || run) return { inst, p, hit: mark?.support ?? null, mark, run, runIndex: mark?.run ?? on?.run ?? -1 };
    if (room) return null; // the room on top takes the press
  }
  return null;
}

/** Plan polygons for the benchAddHover channel (the instance's local frame). */
export function supportHoverPolys(inst: ProductInstance, polys: Vec2[][]): Vec2[][] {
  return inst.category === 'saunabench' ? polys.map((poly) => poly.map((p) => toInstanceLocal(inst, p))) : polys;
}

/** The supports a press (and drag) on `run` places — an end within SNAP_PX of
 *  the run's own end flush with it, unless `free` (Alt) — leaving out any where
 *  one already stands on the same bench (within a trestle's width). The click
 *  and its green preview both read this, so the preview never promises a support
 *  the click will not place. */
function supportsForRow(inst: ProductInstance, run: SupportRun, s0: number, s1: number, mmPerPx: number, free = false): BenchSupport[] {
  const one = Math.abs(s1 - s0) < 4 * mmPerPx;
  const row = supportsAlong(run, s0, one ? s0 : s1, free ? -Infinity : SNAP_PX * mmPerPx).map((s, k) => ({ ...s, id: `${newId('bs')}-${k}` }));
  const taken = supportMarksOf(inst);
  return row.filter((a) => !supportMarksOf(inst, [a]).some((m) => m.support === a.id
    && taken.some((t) => t.bench === m.bench && distance(markCentre(t.poly), markCentre(m.poly)) < TRESTLE_WIDTH)));
}

/** `list` (`inst`'s supports, or what they are about to be) with those standing
 *  on bench run `runIndex` (`sr`) spread evenly along it (`evenSupports`: the
 *  first two flush with its ends, the rest evenly between) — `adds` more of them,
 *  each where it was pressed — keeping their ids, in the order they stand. The
 *  new list and the ids on the run. */
function spreadSupports(inst: ProductInstance, list: BenchSupport[], runIndex: number, sr: SupportRun, adds: Array<{ id: string; at: number }> = []): { list: BenchSupport[]; ids: string[] } {
  const on = new Map<string, number>();
  for (const m of benchSupportMarks({ ...inst, benchSupports: list }, { benchTierHeights: inst.benchTierHeights })) {
    if (m.run === runIndex && !on.has(m.support)) on.set(m.support, m.at);
  }
  const order = [...[...on].map(([id, at]) => ({ id, at })), ...adds].sort((a, b) => a.at - b.at);
  if (!order.length) return { list, ids: [] };
  const places = new Map(evenSupports(sr, order.length, order[0].at).map((p, k) => [order[k].id, p]));
  const out = list.map((sup) => { const p = places.get(sup.id); return p ? { id: sup.id, ...p } : sup; });
  for (const a of adds) out.push({ id: a.id, ...places.get(a.id)! });
  return { list: out, ids: order.map((o) => o.id) };
}

/** What a Bench support tool press on bench run `runIndex` (`sr`), dragged from
 *  station `s0` to `s1`, makes of `inst`'s supports: one more (a drag, a row's
 *  worth — `supportRow`), and the run's supports spread evenly along it; with
 *  `free` (Alt), the row placed where pressed, nothing else moved. The new list,
 *  the ids added and the ids it shows (every one on the run, or the row). */
export function supportsAfterPress(inst: ProductInstance, runIndex: number, sr: SupportRun, s0: number, s1: number, free: boolean, mmPerPx: number): { list: BenchSupport[]; added: string[]; shown: string[] } {
  const list = inst.benchSupports ?? [];
  if (free) {
    const row = supportsForRow(inst, sr, s0, s1, mmPerPx, true), ids = row.map((a) => a.id);
    return { list: [...list, ...row], added: ids, shown: ids };
  }
  const one = Math.abs(s1 - s0) < 4 * mmPerPx;
  const adds = (one ? [s0] : supportRow(s0, s1)).map((at, k) => ({ id: `${newId('bs')}-${k}`, at }));
  const spread = spreadSupports(inst, list, runIndex, sr, adds);
  return { list: spread.list, added: adds.map((a) => a.id), shown: spread.ids };
}

/** The Bench support tool's hover: red over a placed support (a click removes
 *  it), green where a click would leave the trestles of the bench under it —
 *  every tier they stand under. */
export function supportHoverAt(aim: ToolAim): BenchHover | null {
  const world = aim.world;
  const t = supportTargetAt(world, null, aim.mmPerPx);
  if (!t) return null;
  if (t.hit) {
    const polys = supportMarksOf(t.inst).filter((m) => m.support === t.hit).map((m) => m.poly);
    return { instId: t.inst.id, polys: supportHoverPolys(t.inst, polys), kind: 'delete' };
  }
  if (!t.run) return null;
  const s = t.run.stationOf(t.p);
  return supportPreview(t.inst, supportsAfterPress(t.inst, t.runIndex, t.run, s, s, benchSupportTool.supportAlt, aim.mmPerPx));
}

/** Green: where the trestles a press shows (`shown`) would stand. */
function supportPreview(inst: ProductInstance, res: { list: BenchSupport[]; shown: string[] }): BenchHover | null {
  const ids = new Set(res.shown);
  const polys = benchSupportMarks({ ...inst, benchSupports: res.list }, { benchTierHeights: inst.benchTierHeights }).filter((m) => ids.has(m.support)).map((m) => m.poly);
  return polys.length ? { instId: inst.id, polys: supportHoverPolys(inst, polys), kind: 'add' } : null;
}

/** The row the Bench support tool's drag would place, previewed. */
function supportRowHover(): BenchHover | null {
  const d = benchSupportTool.supportRowDrag;
  const inst = d ? state.drawing.instances.find((i) => i.id === d.instId) : null;
  return d && inst ? supportPreview(inst, supportsAfterPress(inst, d.runIndex, d.run, d.s0, d.s1, benchSupportTool.supportAlt, d.mmPerPx)) : null;
}

/** Take support `id` off `inst`, and spread the rest of the run its trestle
 *  `runIndex` stands on (else its first) evenly again — as an added one spreads them. */
export function removeSupport(inst: ProductInstance, id: string, runIndex?: number): void {
  const marks = supportMarksOf(inst).filter((m) => m.support === id && m.sr);
  const own = marks.find((m) => m.run === runIndex) ?? marks[0];
  const rest = (inst.benchSupports ?? []).filter((s) => s.id !== id);
  inst.benchSupports = own?.sr ? spreadSupports(inst, rest, own.run, own.sr).list : rest;
  if (!inst.benchSupports.length) delete inst.benchSupports;
  if (featureOf(state, 'support')?.id === id) dropFeature(state, 'support');
}

/** The middle of a trestle's footprint. */
const markCentre = (poly: Vec2[]): Vec2 => poly.reduce((a, p) => ({ x: a.x + p.x / poly.length, y: a.y + p.y / poly.length }), { x: 0, y: 0 });

/** Start sliding the support whose trestle `mark` was pressed at `sp`, along the
 *  run that trestle stands on — never one looked up from a point. */
function startSupportMove(inst: ProductInstance, mark: TrestleMark, sp: Vec2, removeOnClick: boolean): void {
  const sup = inst.benchSupports?.find((s) => s.id === mark.support);
  if (!mark.sr || !sup) return;
  benchSupportTool.supportMoveDrag = { instId: inst.id, id: mark.support, run: mark.sr, runIndex: mark.run, press: sp, moved: false, removeOnClick, start: { ...sup } };
  startDrag('support-move');
}

/** Put a slid support back where the slide found it (Esc); true when one was live. */
export function cancelSupportMove(): boolean {
  const d = benchSupportTool.supportMoveDrag;
  if (!d) return false;
  const inst = state.drawing.instances.find((i) => i.id === d.instId);
  const sup = inst?.benchSupports?.find((s) => s.id === d.id);
  if (sup) { for (const k of Object.keys(sup) as Array<keyof BenchSupport>) delete sup[k]; Object.assign(sup, d.start); }
  benchSupportTool.supportMoveDrag = null;
  if (state.dragMode === 'support-move') dropDrag();
  invalidate();
  return true;
}

/** The support figures of this frame (world), to the nearest trestle or the
 *  bench's end on each side of one trestle — kept in `supportFiguresShown`. */
export function supportSpacingsNow(): SupportFigure[] | null {
  const figs = supportFigures();
  supportFiguresShown = figs ?? [];
  return figs;
}

/** Whose figures show: the support being slid (on the run of the trestle
 *  pressed); under the Bench support tool, the one a click — or the row being
 *  dragged, at its end under the cursor — would place; else the selected
 *  support's, which a double-click retypes (`edit`). */
function supportFigures(): SupportFigure[] | null {
  const find = (id: string): ProductInstance | undefined => state.drawing.instances.find((i) => i.id === id);
  const figures = (inst: ProductInstance, marks: TrestleMark[], own: TrestleMark, edit: boolean): SupportFigure[] =>
    supportSpacings(marks, own).map((f) => ({
      a: supportWorld(inst, f.a), b: supportWorld(inst, f.b), length: f.length,
      ...(edit ? { edit: { instId: inst.id, id: own.support, run: own.run, from: f.from, side: f.side } } : {}),
    }));
  if (state.dragMode === 'support-move') {
    const d = benchSupportTool.supportMoveDrag, inst = d ? find(d.instId) : undefined;
    if (!d || !inst) return null;
    const marks = supportMarksOf(inst);
    const own = marks.find((m) => m.support === d.id && m.run === d.runIndex);
    return own ? figures(inst, marks, own, false) : null;
  }
  if (state.tool === 'bench-support') {
    const r = benchSupportTool.supportRowDrag, aim = benchSupportTool.supportAim, cur = aim?.world ?? null;
    let inst: ProductInstance | undefined, res: ReturnType<typeof supportsAfterPress> | null = null;
    if (r) {
      inst = find(r.instId);
      if (inst) res = supportsAfterPress(inst, r.runIndex, r.run, r.s0, r.s1, benchSupportTool.supportAlt, r.mmPerPx);
    } else if (aim && state.dragMode === 'none') {
      const t = supportTargetAt(aim.world, null, aim.mmPerPx);
      if (t && !t.hit && t.run) { inst = t.inst; const s = t.run.stationOf(t.p); res = supportsAfterPress(t.inst, t.runIndex, t.run, s, s, benchSupportTool.supportAlt, aim.mmPerPx); }
    }
    if (!inst || !cur || !res?.added.length) return null;
    const ids = new Set(res.added), p = supportFrame(inst, cur);
    const marks = benchSupportMarks({ ...inst, benchSupports: res.list }, { benchTierHeights: inst.benchTierHeights });
    let own: TrestleMark | null = null, best = Infinity;
    for (const m of marks) {
      if (!ids.has(m.support)) continue;
      const c = markCentre(m.poly), d = Math.hypot(c.x - p.x, c.y - p.y);
      if (d < best) { best = d; own = m; }
    }
    return own ? figures(inst, marks, own, false) : null;
  }
  const sel = state.tool === 'select' && state.dragMode === 'none' ? selectedSupportNow() : null;
  if (!sel) return null;
  const own = supportMark(sel.inst, sel.sup.id, featureOf(state, 'support')?.run);
  return own ? figures(sel.inst, supportMarksOf(sel.inst), own, true) : null;
}

/** The room or bench whose own temporary dimensions yield to its supports'
 *  figures, which share their lanes: while one of its supports is selected or
 *  slid, and while the Bench support tool is armed on it — for the whole time,
 *  so they never flicker as the cursor crosses its benches. */
export function supportQuietDims(): string | null {
  if (state.dragMode === 'support-move' && benchSupportTool.supportMoveDrag) return benchSupportTool.supportMoveDrag.instId;
  if (state.tool === 'bench-support') return state.selectedId;
  return selectedSupportNow()?.inst.id ?? null;
}

/** Retype the selected support's figure `f`: its trestle moves to stand
 *  `value` mm from what the figure measures from (a value past the bench's end
 *  stops it flush there). One undo step; none when it lands where it was. */
export function applySupportFigure(f: SupportFigureEdit, value: number): void {
  const inst = state.drawing.instances.find((i) => i.id === f.instId);
  const sup = inst?.benchSupports?.find((s) => s.id === f.id);
  const own = inst ? supportMark(inst, f.id, f.run) : null;
  if (!inst || !sup || !own?.sr) return;
  beginGesture();
  moveSupportTo(sup, own.sr, f.from + f.side * value, 0.5);
  commitGesture();
  renderProperties(); invalidate();
}

/** `placeSupport`, flush at an end within SNAP_PX by default. */
function moveSupportTo(sup: BenchSupport, run: SupportRun, s: number, tol = SNAP_PX / vp.scale): void {
  placeSupport(sup, run, s, tol);
}

/** The Bench support tool's area (its row's, ui/tools3d.ts): its hover, the
 *  cursor and Alt kept for the row a press would start. */
export function supportAreaAt(e: MouseEvent, aim: ToolAim): BenchHover | null {
  benchSupportTool.supportAlt = e.altKey;
  benchSupportTool.supportAim = aim;
  const sculpted = sculptTrestleHoverAt(aim);
  return sculpted !== undefined ? sculpted : supportHoverAt(aim);
}

export function downBenchSupportTool(e: MouseEvent, sp: Vec2, aim: ToolAim): boolean {
  const world = aim.world;
  if (state.tool === 'bench-support' && e.button === 0) {
    if (downSculptTrestle(sp, aim)) return true;
    const t = supportTargetAt(world, null, aim.mmPerPx);
    if (!t) return true;
    beginGesture();
    selectRoomKeepingFeature(state, t.inst.id);
    if (t.mark) {
      startSupportMove(t.inst, t.mark, sp, true);
    } else if (t.run) {
      const s = t.run.stationOf(t.p);
      benchSupportTool.supportRowDrag = { instId: t.inst.id, run: t.run, runIndex: t.runIndex, s0: s, s1: s, press: sp, mmPerPx: aim.mmPerPx };
      startDrag('support-row');
      state.benchAddHover = supportRowHover();
      updateStatus();
    }
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

export function downSupportMark(sp: Vec2, supHost: ProductInstance | null, supHit: { inst: ProductInstance; p: Vec2; hit: string | null; mark: TrestleMark | null; run: SupportRun | null; } | null): boolean {
  if (supHost && supHit?.mark) {
    beginGesture();
    selectOne(state, 'instance', supHost.id);
    selectFeature(state, { kind: 'support', instId: supHost.id, id: supHit.mark.support, run: supHit.mark.run });
    startSupportMove(supHost, supHit.mark, sp, false);
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

export function dragMoveSupportRow(e: MouseEvent, sp: Vec2, world: Vec2, aim: ToolAim): boolean {
  if (benchSupportTool.supportRowDrag) {
    const inst = state.drawing.instances.find((i) => i.id === benchSupportTool.supportRowDrag!.instId);
    if (inst) {
      benchSupportTool.supportRowDrag.s1 = benchSupportTool.supportRowDrag.run.stationOf(supportFrame(inst, world));
      benchSupportTool.supportAim = aim;
      benchSupportTool.supportRowDrag.mmPerPx = aim.mmPerPx;
      benchSupportTool.supportAlt = e.altKey;
      state.benchAddHover = supportRowHover();
      invalidate();
    }
  }
  return false;
}

export function dragMoveSupportMove(e: MouseEvent, sp: Vec2, world: Vec2, aim: ToolAim): boolean {
  if (benchSupportTool.supportMoveDrag) {
    const d = benchSupportTool.supportMoveDrag;
    const inst = state.drawing.instances.find((i) => i.id === d.instId);
    const sup = inst?.benchSupports?.find((s) => s.id === d.id);
    if (!d.moved && Math.hypot(sp.x - d.press.x, sp.y - d.press.y) >= 4) d.moved = true;
    if (inst && sup && d.moved) {
      // A standalone bench's support may slide round a bend onto the next segment.
      const onto = inst.category === 'saunabench' ? supportRunAt(inst, supportFrame(inst, world)) : null;
      if (onto) { d.run = onto.sr; d.runIndex = onto.run; }
      moveSupportTo(sup, d.run, Math.round(d.run.stationOf(supportFrame(inst, world))), e.altKey ? -Infinity : pxAt(aim, SNAP_PX));
      invalidate();
    }
  }
  return false;
}

/** A selected bench support's panel: what it stands on, where along it (typed,
 *  or flush with the bench's end), and Delete. */
export function renderSupportPanel(body: HTMLElement, inst: ProductInstance, sup: BenchSupport): void {
  body.appendChild(sectionTitle('Bench support'));
  body.appendChild(buttonRow(inst.category === 'saunabench' ? '← Back to bench' : '← Back to room', () => {
    dropFeature(state, 'support');
    renderProperties(); invalidate();
  }));
  const iw = sup.innerWallId != null ? innerWallsOf(inst).findIndex((w) => w.id === sup.innerWallId) : -1;
  body.appendChild(infoRow('On', sup.edge != null ? `${tr('wall')} ${sup.edge + 1}` : sup.corner != null ? `${tr('Rounded corner')} ${sup.corner + 1}`
    : sup.roundBench != null ? tr('Rounded bench end')
    : iw >= 0 ? `${tr('inner wall')} ${iw + 1}` : tr('bench')));
  body.appendChild(infoRow('Tiers', String(supportMarksOf(inst).filter((m) => m.support === sup.id).length)));
  // Read on the trestle its figures show (the tier pressed): its centre from the
  // bench's two ends, as the plan's figures and a pane's panel read.
  const own = supportMark(inst, sup.id, featureOf(state, 'support')?.run);
  const sr = own?.sr;
  if (own && sr) {
    const moveTo = (station: number, tol: number): void => {
      beginGesture();
      moveSupportTo(sup, sr, station, tol);
      commitGesture();
      renderProperties(); invalidate();
    };
    body.appendChild(numberRow('Distance from left (mm)', Math.round(own.at - own.ends[0]), (v) => moveTo(own.ends[0] + v, 0.5), { commit: true }));
    body.appendChild(numberRow('Distance from right (mm)', Math.round(own.ends[1] - own.at), (v) => moveTo(own.ends[1] - v, 0.5), { commit: true }));
    body.appendChild(selectRow('Stands', [['free', 'Where placed'], ['start', 'Flush with the start'], ['end', 'Flush with the end']], sup.flush ?? 'free', (v) => {
      if (v === (sup.flush ?? 'free')) return;
      if (v === 'free') moveTo(own.at, -Infinity);
      else moveTo(v === 'start' ? sr.lo : sr.hi, SNAP_PX / vp.scale);
    }));
  }
  body.appendChild(deleteRow(() => {
    pushHistory();
    removeSupport(inst, sup.id, featureOf(state, 'support')?.run);
    renderProperties(); invalidate();
  }));
}
